import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';

interface FeedbackData {
  musical_content: number;
  sound_quality: number;
  ambience: number;
  selection_of_artists: number;
  overall_experience: number;
}

const Feedback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [ticketNumber, setTicketNumber] = useState<string>('');
  const [ticketSuffix, setTicketSuffix] = useState<string>('');
  const [isValidated, setIsValidated] = useState<boolean>(false);
  const [eventTitle, setEventTitle] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  
  const [feedback, setFeedback] = useState<FeedbackData>({
    musical_content: 3,
    sound_quality: 3,
    ambience: 3,
    selection_of_artists: 3,
    overall_experience: 3,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    const ticketFromUrl = searchParams.get('ticket');
    if (ticketFromUrl && ticketFromUrl.startsWith('ET') && ticketFromUrl.length === 10) {
      const suffix = ticketFromUrl.substring(2);
      setTicketSuffix(suffix);
      handleVerifyTicket(ticketFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Auto-dismiss error after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleTicketSuffixChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 8);
    setTicketSuffix(value);
  };

  const handleVerifyTicket = async (fullTicket?: string) => {
    const ticket = fullTicket || `ET${ticketSuffix}`;
    
    if (ticketSuffix.length !== 8) {
      setError('Please enter the complete 8-character ticket number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_BASE_URL}/feedback/verify-ticket`, {
        ticket_number: ticket
      });

      setTicketNumber(ticket);
      setEventTitle(response.data.event_title);
      setIsValidated(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid ticket number or feedback already submitted');
      setIsValidated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSliderChange = (field: keyof FeedbackData, value: number) => {
    setFeedback(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isValidated) {
      setError('Please verify your ticket number first');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post(`${API_BASE_URL}/feedback/submit`, {
        ticket_number: ticketNumber,
        ...feedback
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit feedback');
    } finally {
      setLoading(false);
    }
  };

  const getRatingLabel = (value: number): string => {
    const labels = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];
    return labels[value];
  };

  const getRatingColor = (value: number): string => {
    if (value <= 2) return 'text-red-600';
    if (value === 3) return 'text-yellow-600';
    return 'text-green-600';
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-warm-100 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="mb-6">
            <svg className="mx-auto h-16 w-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-primary-800 mb-4">Thank You! 🎉</h2>
          <p className="text-secondary-700 mb-2">Your feedback has been submitted successfully.</p>
          <p className="text-secondary-600 text-sm">Redirecting to home page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-warm-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-600 to-secondary-600 px-8 py-6">
            <h1 className="text-3xl font-bold text-white mb-2">Event Feedback</h1>
            <p className="text-primary-100">We'd love to hear about your experience!</p>
          </div>

          {/* Content */}
          <div className="px-8 py-6">
            {!isValidated ? (
              <div>
                <h2 className="text-xl font-semibold text-secondary-800 mb-4">Enter Your Ticket Number</h2>
                <p className="text-secondary-600 mb-6">Please enter your ticket number to provide feedback.</p>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Ticket Number
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-4 py-3 bg-secondary-100 text-secondary-700 font-mono text-lg font-bold rounded-lg border-2 border-secondary-300">
                      ET-
                    </span>
                    <input
                      type="text"
                      value={ticketSuffix}
                      onChange={handleTicketSuffixChange}
                      placeholder="XXXXXXXX"
                      maxLength={8}
                      className="flex-1 px-4 py-3 border-2 border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-lg uppercase"
                    />
                  </div>
                  <p className="text-xs text-secondary-500 mt-2">
                    Enter the 8-character code from your ticket (letters and numbers)
                  </p>
                </div>

                {error && (
                  <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
                    {error}
                  </div>
                )}

                <button
                  onClick={() => handleVerifyTicket()}
                  disabled={loading || ticketSuffix.length !== 8}
                  className="w-full bg-gradient-to-r from-primary-600 to-secondary-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-primary-700 hover:to-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {loading ? 'Verifying...' : 'Verify Ticket'}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded">
                  <p className="text-green-800 font-semibold">✓ Ticket Verified</p>
                  <p className="text-green-700 text-sm mt-1">Event: {eventTitle}</p>
                  <p className="text-green-600 text-xs mt-1">Ticket: {ticketNumber}</p>
                </div>

                <h2 className="text-xl font-semibold text-secondary-800 mb-6">Rate Your Experience</h2>

                {/* Musical Content */}
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm font-medium text-secondary-700">Musical Content</label>
                    <span className={`font-semibold ${getRatingColor(feedback.musical_content)}`}>
                      {getRatingLabel(feedback.musical_content)}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={feedback.musical_content}
                    onChange={(e) => handleSliderChange('musical_content', parseInt(e.target.value))}
                    className="w-full h-2 bg-secondary-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-xs text-secondary-500 mt-1">
                    <span>Poor</span>
                    <span>Excellent</span>
                  </div>
                </div>

                {/* Sound Quality */}
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm font-medium text-secondary-700">Sound Quality</label>
                    <span className={`font-semibold ${getRatingColor(feedback.sound_quality)}`}>
                      {getRatingLabel(feedback.sound_quality)}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={feedback.sound_quality}
                    onChange={(e) => handleSliderChange('sound_quality', parseInt(e.target.value))}
                    className="w-full h-2 bg-secondary-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-xs text-secondary-500 mt-1">
                    <span>Poor</span>
                    <span>Excellent</span>
                  </div>
                </div>

                {/* Ambience */}
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm font-medium text-secondary-700">Ambience</label>
                    <span className={`font-semibold ${getRatingColor(feedback.ambience)}`}>
                      {getRatingLabel(feedback.ambience)}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={feedback.ambience}
                    onChange={(e) => handleSliderChange('ambience', parseInt(e.target.value))}
                    className="w-full h-2 bg-secondary-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-xs text-secondary-500 mt-1">
                    <span>Poor</span>
                    <span>Excellent</span>
                  </div>
                </div>

                {/* Selection of Artists */}
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm font-medium text-secondary-700">Selection of Artists</label>
                    <span className={`font-semibold ${getRatingColor(feedback.selection_of_artists)}`}>
                      {getRatingLabel(feedback.selection_of_artists)}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={feedback.selection_of_artists}
                    onChange={(e) => handleSliderChange('selection_of_artists', parseInt(e.target.value))}
                    className="w-full h-2 bg-secondary-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-xs text-secondary-500 mt-1">
                    <span>Poor</span>
                    <span>Excellent</span>
                  </div>
                </div>

                {/* Overall Experience */}
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm font-medium text-secondary-700">Overall Experience</label>
                    <span className={`font-semibold ${getRatingColor(feedback.overall_experience)}`}>
                      {getRatingLabel(feedback.overall_experience)}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={feedback.overall_experience}
                    onChange={(e) => handleSliderChange('overall_experience', parseInt(e.target.value))}
                    className="w-full h-2 bg-secondary-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-xs text-secondary-500 mt-1">
                    <span>Poor</span>
                    <span>Excellent</span>
                  </div>
                </div>

                {error && (
                  <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
                    {error}
                  </div>
                )}

                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setIsValidated(false);
                      setTicketSuffix('');
                      setTicketNumber('');
                      setError('');
                    }}
                    className="flex-1 bg-secondary-200 text-secondary-700 py-3 px-6 rounded-lg font-semibold hover:bg-secondary-300 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-primary-600 to-secondary-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-primary-700 hover:to-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {loading ? 'Submitting...' : 'Submit Feedback'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-6 text-center text-secondary-600 text-sm">
          <p>Your feedback helps us improve future events. Thank you for your time!</p>
        </div>
      </div>

      <style>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        
        .slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
      `}</style>
    </div>
  );
};

export default Feedback;
